import type { ProtectedRouteOptions } from "../../routing-services";
import { ExtendedUserInfo, useAuthentication } from "../authentication";
import { configureRouter } from "@/.shared/services/routing-services";
import NotAuthorizedView from "@/.shared/views/NotAuthorizedView.vue";
import { useIntl } from "../../i18n";
import type { RouteLocationNormalized } from "vue-router";


export interface AuthorizationService {
    getUserPermissions(): Iterable<string>
    hasPermission(permission: string): boolean;
}

export interface AuthorizationServiceConfiguration {
    commonRoutePermissionChecker?: (userInfo: ExtendedUserInfo, route: RouteLocationNormalized) => boolean;
}

export class AuthorizationServiceImpl implements AuthorizationService {
    private _auth: ReturnType<typeof useAuthentication>;

    constructor(cfg: AuthorizationServiceConfiguration) {
        this._auth = useAuthentication();
    }

    public *getUserPermissions(): Iterable<string> {
        for (const permission of this.userInfo.permissions ?? []) {
            yield permission;
        }
    }

    public hasPermission(permission: string) {
        return this.userInfo.permissions?.has(permission) ?? false;
    }

    get userInfo() : ExtendedUserInfo {
        return this._auth.userInfo as ExtendedUserInfo;
    }
}

let instance: AuthorizationServiceImpl;



export function configureAuthorization(cfg: AuthorizationServiceConfiguration) {
    instance = new AuthorizationServiceImpl(cfg);
    configureRouter(router => {

        router.addRoute("not-authorized", b => b.withPath("not-authorized").withComponent(NotAuthorizedView).allowAnonymous());

        router.beforeEach(async (to, from, next) => {        
            let prot = to.meta.protection as ProtectedRouteOptions;
            let destination = to.fullPath
            const isRedirecting = to.query.__firstRedirect === "true";

            const authentication = useAuthentication();
            const check = (resolve: (...args: any[]) => void) => {
                if (authentication.isInititalized) {
                    resolve();
                }
                else {
                    setTimeout(() => check(resolve), 50);
                }
            }
           
            await new Promise(resolve => check(resolve));
            
            if (isRedirecting) {
                const url = new window.URL("https://dummy" + to.fullPath);
                url.searchParams.delete("__firstRedirect");
                destination = `${url.pathname}${url.search}${url.hash}`;
            }

            let canHaveAccess;
            let current = to;
            while (prot != null) {
           
                //Verificamos si el usuario está logueado y gestionamos redirecciones
                canHaveAccess = canHaveAccess?? authentication.isLoggedIn();
                let isInitialLoad = from.matched.length == 0;

                if (!canHaveAccess) {
                    if (isInitialLoad && !isRedirecting) {
                        const url = new window.URL("https://dummy" + to.fullPath);
                        url.searchParams.append("__firstRedirect", "true");
                        authentication.login(`${url.pathname}${url.search}${url.hash}`);
                    }
                    else
                        return next(useIntl().i18Route({ name: "home", query: { redirectTo: destination } }));
                }

                //Aplicamos políticas comunes
                if (canHaveAccess && cfg.commonRoutePermissionChecker) {
                    canHaveAccess = cfg.commonRoutePermissionChecker(instance.userInfo, to);
                }

                //Aplicamos políticas específicas de la ruta
                if (canHaveAccess) {
                    if (typeof prot.authReqs === "function") {
                        canHaveAccess = prot.authReqs(instance.userInfo, to);
                    }
                    else if (Array.isArray(prot.authReqs)) {
                        for (const permission of prot.authReqs) {
                            if (!instance.hasPermission(permission)) {
                                canHaveAccess = false;
                                break;
                            }
                        }
                    }
                }

                if (canHaveAccess === false)
                    break;

                current = (current.meta.parent as any);
                prot = current?.meta.protection as ProtectedRouteOptions;
            }

            if (canHaveAccess === false)
                return next(useIntl().i18Route({ name: "not-authorized", query: { redirectTo: "/" } }));

            if (destination !== to.fullPath)
                next(destination);
            else
                next();
        });
    })

}

export function useAuthorization(): AuthorizationService {
    return instance;
}

export enum PermissionsApp {
    Administracion = 'ADMINISTRACION',
    
    // AccesoInstalaciones:
    AccesoInstalacionesTodas = 'ACCESO_INSTALACIONES.TODAS',
    AccesoInstalacionesAsignadas='ACCESO_INSTALACIONES.ASIGNADAS',
    
    // Regiones:
    RegionesVer = 'REGIONES.VER',
    
    // Zonas:
    ZonasVer = 'ZONAS.VER',
    ZonasEditar = 'ZONAS.EDITAR',
    
    // Instalaciones:
    InstalacionesVer = 'INSTALACIONES.VER',
    InstalacionesEditar = 'INSTALACIONES.EDITAR',
    
    // Oscilos:
    OscilosVer = 'OSCILOS.VER',
    OscilosAnadir = 'OSCILOS.ANADIR',
    OscilosEditar = 'OSCILOS.EDITAR',
    OscilosEliminar = 'OSCILOS.ELIMINAR',
    OscilosEncolarPeticionManualDescarga = 'OSCILOS.ENCOLAR_PETICION_MANUAL_DESCARGA',
    
    // Sucesos:
    SucesosVer = 'SUCESOS.VER',
    SucesosAnadir = 'SUCESOS.ANADIR',
    SucesosEditar = 'SUCESOS.EDITAR',
    
    // Ficheros de medida:
    FicherosMedidaVer = 'FICHEROS_MEDIDA.VER',
    FicherosMedidaDescargar = 'FICHEROS_MEDIDA.DESCARGAR',
    
    // Usuarios:
    UsuariosVer = 'USUARIOS.VER',
    UsuariosAnadir = 'USUARIOS.ANADIR',
    UsuariosEditar = 'USUARIOS.EDITAR',
    UsuariosEliminar = 'USUARIOS.ELIMINAR',
    
    // Portal:
    PortalVisorOscilos = 'PORTAL.VISOR_OSCILOS',
    PortalWikiSucesos = 'PORTAL.WIKI_SUCESOS',
    PortalAdministracion = 'PORTAL.ADMINISTRACION'
}
// <li v-if="tienePermisoPortalVisorOscilos" class="nav-item">